/**
 * Page Registry — the authoritative list of every UI page this build
 * ships and the permission it requires.
 *
 * On first login (see App.tsx), this list is POSTed to the backend's
 * /api/access/pages endpoint. The backend marks any previously-registered
 * page missing from this batch as inactive. This keeps the Access Manager
 * page_registry table precisely in sync with the deployed frontend.
 *
 * HOW TO ADD A PAGE:
 *   1. Add its route to App.tsx
 *   2. Add an entry here with the required permission
 *   3. Add the corresponding nav item in acc-ui/components/Sidebar.tsx
 *   4. Permission key must already exist in shared/constants/permissions.ts
 *
 * DO NOT add a page to App.tsx without adding it here. CI's page audit
 * (future) will fail the build.
 */

import {
  DASHBOARD_VIEW,
  INGESTION_VIEW,
  PARSING_VIEW,
  VALIDATION_VIEW,
  REPORTING_VIEW,
  DETECTION_VIEW,
  ACCESS_VIEW,
  ACCESS_VIEW_PAGE_REGISTRY,
  MASTERS_VIEW,
  ADMIN_VIEW,
  ADMIN_EDIT_CONFIG,
  SETTINGS_VIEW,
  NOTIFICATIONS_VIEW,
  NOTIFICATIONS_MANAGE_PREFS,
} from '@loanaudit/shared';

export interface PageRegistryEntry {
  path: string;
  title: string;
  module: string;
  requiredPermission: string;
}

export const PAGE_REGISTRY: PageRegistryEntry[] = [
  // Sprint 1
  { path: '/',                         title: 'Dashboard',          module: 'dashboard', requiredPermission: DASHBOARD_VIEW },

  // Sprint 2
  { path: '/admin/team',               title: 'Team',               module: 'access',  requiredPermission: ACCESS_VIEW },
  { path: '/admin/access-manager',     title: 'Access Manager',     module: 'access',  requiredPermission: ACCESS_VIEW },
  { path: '/admin/page-registry',      title: 'Page Registry',      module: 'access',  requiredPermission: ACCESS_VIEW_PAGE_REGISTRY },
  { path: '/admin/masters',            title: 'Masters',            module: 'masters', requiredPermission: MASTERS_VIEW },
  { path: '/admin/system',             title: 'System Admin',       module: 'admin',   requiredPermission: ADMIN_VIEW },
  { path: '/settings',                 title: 'Settings',           module: 'settings',requiredPermission: SETTINGS_VIEW },

  // Sprint 3
  { path: '/admin/config',             title: 'Config',             module: 'admin',         requiredPermission: ADMIN_EDIT_CONFIG },
  { path: '/notifications',            title: 'Notifications',      module: 'notifications', requiredPermission: NOTIFICATIONS_VIEW },
  { path: '/settings/notifications',   title: 'Notification prefs', module: 'notifications', requiredPermission: NOTIFICATIONS_MANAGE_PREFS },

  // Sprint 4 — ingestion
  { path: '/loan-files',               title: 'Loan Files',         module: 'ingestion', requiredPermission: INGESTION_VIEW },
  { path: '/loan-files/new',           title: 'Upload Loan File',   module: 'ingestion', requiredPermission: INGESTION_VIEW },
  { path: '/loan-files/:id',           title: 'Loan File Detail',   module: 'ingestion', requiredPermission: INGESTION_VIEW },

  // Sprint 5 — parsing
  { path: '/parsing/review-queue',            title: 'Review Queue',       module: 'parsing',   requiredPermission: PARSING_VIEW },
  { path: '/parsing/review-queue/:reviewId',  title: 'Review Item',        module: 'parsing',   requiredPermission: PARSING_VIEW },

  // Sprint 6 — validation
  { path: '/findings',                  title: 'Findings',            module: 'validation', requiredPermission: VALIDATION_VIEW },
  { path: '/findings/:findingId',       title: 'Finding Detail',      module: 'validation', requiredPermission: VALIDATION_VIEW },

  // Sprint 7+ placeholders
  { path: '/reports',                  title: 'Reports',             module: 'reporting',  requiredPermission: REPORTING_VIEW },
  { path: '/analytics',                title: 'Analytics',           module: 'detection',  requiredPermission: DETECTION_VIEW },
  { path: '/runs',                     title: 'Engine Runs',         module: 'ingestion',  requiredPermission: INGESTION_VIEW },
];
